import React, { useState, useRef } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  CloseButton,
  FormControl,
  FormLabel,
  useToast,
  Heading,
  Input,
  Text,
  Select,
  Grid,
  GridItem,
  Image,
  Flex,
} from "@chakra-ui/react";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import LogoComponent from "../Logo/LogoComponent";
import { cqAPI } from "../../lib/Auth";

export default function Signup() {
  const toast = useToast();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [practiceManagementSoftware, setPracticeManagementSoftware] = useState("");
  const [otherSoftwareName, setOtherSoftwareName] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [mapImageUrl, setMapImageUrl] = useState("");

  // State for alerts
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertStatus, setAlertStatus] = useState("error");

  // State for submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAP_API,
    libraries: ["places"],
  });

  const autocompleteRef = useRef(null);

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    const isDentalOffice = place.types && place.types.includes("dentist");

    if (!isDentalOffice) {
      toast({
        title: "Invalid selection.",
        description: "You can only select a dental office.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      // Clear the input field and map
      setInputValue("");
      setSelectedPlace(null);
      setMapImageUrl("");
      return;
    }

    setSelectedPlace(place);
    setInputValue(place.name);

    // Generate the map image URL with higher zoom and smaller size
    const location = place.geometry.location;
    const lat = location.lat();
    const lng = location.lng();
    const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=17&size=600x200&markers=color:red%7C${lat},${lng}&key=${process.env.REACT_APP_MAP_API}`;
    setMapImageUrl(mapUrl);
  };

  const handleSoftwareChange = (e) => {
    setPracticeManagementSoftware(e.target.value);
    if (e.target.value !== "Other") {
      setOtherSoftwareName("");
    }
  };

  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFullNameValid = (name) => {
    return name.trim().split(" ").length >= 2;
  };

  const showAlert = (message, status = "error") => {
    setAlertMessage(message);
    setAlertStatus(status);
    setIsAlertVisible(true);
  };

  const hideAlert = () => {
    setIsAlertVisible(false);
    setAlertMessage("");
  };

  const customSignUp = async (event) => {
    event.preventDefault();

    // Validation
    if (!isFullNameValid(fullName)) {
      showAlert("Please enter a valid full name.");
      return;
    }
    if (!isEmailValid(email)) {
      showAlert("Please enter a valid email address.");
      return;
    }
    if (!selectedPlace) {
      showAlert("Please select your office from the autocomplete.");
      return;
    }
    if (!practiceManagementSoftware) {
      showAlert("Please select a practice management software.");
      return;
    }
    if (practiceManagementSoftware === "Other" && !otherSoftwareName) {
      showAlert("Please enter the name of your practice management software.");
      return;
    }

    hideAlert();
    setIsSubmitting(true);

    // Prepare data to send
    const data = {
      name: fullName,
      email,
      place: selectedPlace,
      pms:
        practiceManagementSoftware === "Other"
          ? otherSoftwareName
          : practiceManagementSoftware,
    };

    try {
      const response = await cqAPI.post("/signup", data);

      // Handle response
      if (response.type === "error") {
        showAlert(response.msg);
        setIsSubmitting(false);
        return;
      }

      // Successful submission
      setIsSubmitting(false);
      setIsSubmitted(true);
    } catch (error) {
      showAlert(`Error during sign up: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  // Determine if form fields should be disabled
  const isFormDisabled = isSubmitted;

  return (
    <Box height="100vh" overflowY="auto" bg="gray.50">
      <Box
        display="flex"
        marginTop="100px"
        marginBottom="200px"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        flex="1"
        px={4}
      >
        <Box
          width="100%"
          maxWidth={{ base: "100%", sm: "600px", md: "700px", lg: "800px" }}
          mt="auto"
          mb="auto"
        >
          {isSubmitted ? (
            <Box textAlign="center" px={4}>
              <Heading
                as="h2"
                fontSize={{ base: "24px", md: "28px", lg: "32px" }}
                fontWeight="700"
                color="teal.600"
                lineHeight="140%"
              >
                Thank You!
              </Heading>
              <Text mt={4} fontSize={{ base: "md", md: "lg" }} color="gray.600">
                Your information has been successfully submitted. Our team will contact you
                shortly to guide you through the onboarding process.
              </Text>
            </Box>
          ) : (
            <Box
              width="100%"
              borderWidth={1}
              borderRadius={8}
              boxShadow="lg"
              bg="white"
              p={8}
            >
              <LogoComponent
                isCollapsed={false}
                boxSize="5rem"
                mx="auto"
                mb={4}
              ></LogoComponent>
              <Box textAlign="center">
                <Heading
                  as="h1"
                  fontSize={{ base: "20px", md: "24px", lg: "28px" }}
                  fontWeight="700"
                  color="teal.600"
                  lineHeight="140%"
                >
                  Sign Up
                </Heading>
                <Text
                  mt={2}
                  fontSize={{ base: "sm", md: "md" }}
                  maxWidth="600px"
                  mx="auto"
                  color="gray.600"
                >
                  Ready to get started? Please provide your details below, and our team will
                  contact you shortly to guide you through the onboarding process.
                </Text>
              </Box>
              <Box my={6} textAlign="left">
                <form onSubmit={customSignUp}>
                  <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
                    <GridItem>
                      <FormControl isRequired isDisabled={isFormDisabled}>
                        <FormLabel>Full Name</FormLabel>
                        <Input
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          focusBorderColor="teal.500"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired isDisabled={isFormDisabled}>
                        <FormLabel>Email Address</FormLabel>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          focusBorderColor="teal.500"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired isDisabled={isFormDisabled}>
                        <FormLabel>Office Name</FormLabel>
                        {isMapsLoaded && (
                          <Autocomplete
                            onLoad={(autocomplete) => {
                              autocompleteRef.current = autocomplete;
                            }}
                            onPlaceChanged={handlePlaceSelect}
                          >
                            <Input
                              type="text"
                              placeholder="Type your office name and address"
                              value={inputValue}
                              onChange={(e) => setInputValue(e.target.value)}
                              focusBorderColor="teal.500"
                              isDisabled={isFormDisabled}
                            />
                          </Autocomplete>
                        )}
                        {!isMapsLoaded && (
                          <Input
                            type="text"
                            placeholder="Loading Maps..."
                            disabled
                            focusBorderColor="teal.500"
                          />
                        )}
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired isDisabled={isFormDisabled}>
                        <FormLabel>Practice Management Software</FormLabel>
                        <Select
                          value={practiceManagementSoftware}
                          onChange={handleSoftwareChange}
                          focusBorderColor="teal.500"
                        >
                          <option value="">Select an option</option>
                          <option value="Dentrix">Dentrix</option>
                          <option value="Open Dental">Open Dental</option>
                          <option value="EagleSoft">EagleSoft</option>
                          <option value="Denticon">Denticon</option>
                          <option value="Other">Other</option>
                        </Select>
                      </FormControl>
                    </GridItem>
                    {practiceManagementSoftware === "Other" && (
                      <GridItem>
                        <FormControl isRequired isDisabled={isFormDisabled}>
                          <FormLabel>Please Specify</FormLabel>
                          <Input
                            type="text"
                            value={otherSoftwareName}
                            onChange={(e) => setOtherSoftwareName(e.target.value)}
                            focusBorderColor="teal.500"
                          />
                        </FormControl>
                      </GridItem>
                    )}
                    {practiceManagementSoftware !== "Other" && (
                      <GridItem>
                        <Box></Box>
                      </GridItem>
                    )}
                    {/* Display the map if a valid place is selected */}
                    {mapImageUrl && (
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <Box mt={4} textAlign="center">
                          <Image
                            src={mapImageUrl}
                            alt="Selected Place"
                            borderRadius="md"
                            width="100%"
                          />
                        </Box>
                      </GridItem>
                    )}
                  </Grid>
                  {isAlertVisible && (
                    <Alert status={alertStatus} mt={4} borderRadius="md">
                      <AlertIcon />
                      <Box flex="1">
                        <AlertTitle>
                          <Text casing="capitalize">{alertStatus}</Text>
                        </AlertTitle>
                        <AlertDescription display="block">{alertMessage}</AlertDescription>
                      </Box>
                      <CloseButton
                        position="absolute"
                        right="8px"
                        top="8px"
                        onClick={hideAlert}
                      />
                    </Alert>
                  )}
                  {!isSubmitted && (
                    <Flex justifyContent="center" mt={6}>
                      <Button
                        colorScheme="teal"
                        width="240px"
                        type="submit"
                        isLoading={isSubmitting}
                        isDisabled={isSubmitting}
                      >
                        Sign Up
                      </Button>
                    </Flex>
                  )}
                </form>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
